<template>
  <v-dialog
    v-model="isOpen"
    max-width="1000px"
    :persistent="loading"
    @click:outside="closeModal">
    <v-card>
      <v-card-title>
        <span class="text-h5">Import Task Date</span>
      </v-card-title>

      <v-card-text>
        <p class="caption mb-2">
          ใช้ไฟล์จากปุ่ม EXPORT FOR UPDATE
        </p>
        <v-file-input
          :key="componentKey"
          v-model="selectedFile"
          label="Choose CSV file"
          accept=".csv"
          dense
          outlined
          hide-details
          :disabled="loading"
          @change="handlePreview" />
      </v-card-text>

      <v-alert
        v-if="fileError"
        type="error"
        dense
        text
        class="mx-4">
        {{ fileError }}
      </v-alert>

      <template v-else-if="hasPreview">
        <v-alert
          v-for="warning in fileWarnings"
          :key="warning"
          type="warning"
          dense
          text
          class="mx-4">
          {{ warning }}
        </v-alert>
        <v-alert
          :type="summary.error ? 'error' : 'info'"
          dense
          text
          class="mx-4">
          เปลี่ยน {{ summary.change }} รายการ · ไม่เปลี่ยน {{ unchangedCount }} · ข้าม {{ summary.skip }} · error {{ summary.error }}
          <template v-if="summary.error">
            — กรุณาแก้ไขไฟล์ก่อน Import
          </template>
        </v-alert>

        <v-data-table
          v-if="previewItems.length"
          :headers="tableHeaders"
          :items="previewItems"
          item-key="key"
          :items-per-page="-1"
          hide-default-footer
          fixed-header
          height="320"
          dense
          class="elevation-1 mx-4 mb-4">
          <template v-slot:item.start="{ item }">
            <span v-if="item.newStart">
              {{ item.oldStart || '-' }} → <b>{{ item.newStart }}</b>
            </span>
            <span v-else>{{ item.oldStart || '-' }}</span>
          </template>
          <template v-slot:item.due="{ item }">
            <span v-if="item.newDue">
              {{ item.oldDue || '-' }} → <b>{{ item.newDue }}</b>
            </span>
            <span v-else>{{ item.oldDue || '-' }}</span>
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip
              x-small
              dark
              :color="typeStyles[item.type].color">
              {{ typeStyles[item.type].text }}
            </v-chip>
            <span
              v-if="item.message"
              class="caption ml-1"
              :class="item.type === 'error' ? 'error--text' : 'warning--text'">
              {{ item.message }}
            </span>
          </template>
        </v-data-table>

        <v-card-text
          v-if="summary.change"
          class="pt-0">
          <v-textarea
            v-model="note"
            label="Note"
            rows="2"
            auto-grow
            outlined
            hide-details="auto"
            placeholder="Add a note"
            :disabled="loading"
            :rules="[v => !!(v && v.trim()) || 'กรุณาระบุโน๊ต']" />
        </v-card-text>
      </template>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="error"
          :disabled="loading"
          @click="closeModal">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!canImport"
          :loading="loading"
          @click="handleImport">
          Import ({{ summary.change }})
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const SKETCH_ID_HEADER = 'sketch id'
const DATE_FIELDS = { start: 'startDate', due: 'dueDate' }
const DISPLAY_FORMAT = 'DD/MM/YYYY'

const isBlank = (value) => !value || value.trim().toLowerCase() === 'no task'

export default {
  props: {
    prototypes: {
      type: Array,
      default: () => []
    },
    stageColumns: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpen: true,
      selectedFile: null,
      componentKey: 0,
      hasPreview: false,
      fileError: '',
      fileWarnings: [],
      previewItems: [],
      unchangedCount: 0,
      note: '',
      tableHeaders: [
        { text: 'Sketch ID', value: 'sketchId', sortable: false, width: 120 },
        { text: 'Stage', value: 'stage', sortable: false, width: 180 },
        { text: 'Start', value: 'start', sortable: false, width: 210 },
        { text: 'Due', value: 'due', sortable: false, width: 210 },
        { text: 'สถานะ', value: 'type', sortable: false, width: 60 }
      ],
      typeStyles: {
        change: { text: 'เปลี่ยน', color: 'primary' },
        skip: { text: 'ข้าม', color: 'grey' },
        error: { text: 'Error', color: 'error' }
      }
    }
  },
  computed: {
    summary () {
      return this.previewItems.reduce((acc, item) => {
        acc[item.type] += 1
        return acc
      }, { change: 0, skip: 0, error: 0 })
    },
    canImport () {
      return !this.loading
        && this.hasPreview
        && !this.fileError
        && this.summary.error === 0
        && this.summary.change > 0
        && !!this.note.trim()
    }
  },
  methods: {
    parseDate (value) {
      const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(value)
      const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value)
      let parts = null
      if (dmy) parts = [dmy[3], dmy[2], dmy[1]]
      else if (ymd) parts = [ymd[1], ymd[2], ymd[3]]
      if (!parts) return null

      const [rawYear, month, day] = parts.map(Number)
      let year = rawYear < 100 ? 2000 + rawYear : rawYear
      if (year > 2400) year -= 543
      const date = new Date(year, month - 1, day)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null

      return this.$dayjs(date)
    },
    formatDate (date) {
      return date ? this.$dayjs(date).format(DISPLAY_FORMAT) : ''
    },
    resetPreview () {
      this.hasPreview = false
      this.fileError = ''
      this.fileWarnings = []
      this.previewItems = []
      this.unchangedCount = 0
    },
    async handlePreview () {
      this.resetPreview()
      if (!this.selectedFile) return

      try {
        const text = await this.selectedFile.text()
        const { headers, rows } = this.$parseCsv(text.replace(/^\uFEFF/, ''))
        this.processRows(headers || [], rows || [])
      } catch (error) {
        console.error('TaskDateImport readFile :', error)
        this.fileError = 'ไม่สามารถอ่านไฟล์ได้ กรุณาตรวจสอบ format'
      }
      this.hasPreview = true
    },
    buildColumnMap (headers) {
      const labelMap = {}
      this.stageColumns.forEach((stage) => {
        Object.entries(DATE_FIELDS).forEach(([suffix, field]) => {
          labelMap[`${stage.label} - ${suffix}`.toLowerCase()] = { stageId: stage.id, label: stage.label, field }
        })
      })

      const columnMap = {}
      const unknownColumns = []
      headers.forEach((header) => {
        const mapping = labelMap[header.toLowerCase()]
        if (mapping) columnMap[header] = mapping
        else if (/ - (start|due)$/i.test(header)) unknownColumns.push(header)
      })

      return { columnMap, unknownColumns }
    },
    processRows (headers, rows) {
      if (!rows.length) {
        this.fileError = 'ไม่พบข้อมูลใน CSV'
        return
      }

      const sketchHeader = headers.find((header) => header.toLowerCase() === SKETCH_ID_HEADER)
      if (!sketchHeader) {
        this.fileError = 'ไฟล์ไม่ถูกต้อง: ไม่พบ column "Sketch ID"'
        return
      }

      const { columnMap, unknownColumns } = this.buildColumnMap(headers)
      if (!Object.keys(columnMap).length) {
        this.fileError = 'ไม่พบ column ของ stage ในไฟล์ (เช่น "Sampling (Merchandiser) - Start")'
        return
      }
      if (unknownColumns.length) {
        this.fileWarnings.push(`ไม่พบ stage ของ column: ${unknownColumns.join(', ')} (จะไม่ถูก import)`)
      }

      const prototypeMap = new Map(
        this.prototypes.map((prototype) => [String(prototype?.sketch?.sketchId ?? '').trim().toUpperCase(), prototype])
      )
      const sketchIdCount = rows.reduce((acc, row) => {
        const sketchId = (row[sketchHeader] || '').trim().toUpperCase()
        if (sketchId) acc[sketchId] = (acc[sketchId] || 0) + 1
        return acc
      }, {})

      const sketchOrder = rows.reduce((acc, row, rowIndex) => {
        const sketchId = (row[sketchHeader] || '').trim().toUpperCase()
        if (sketchId && acc[sketchId] === undefined) acc[sketchId] = rowIndex
        return acc
      }, {})
      const stageOrder = Object.fromEntries(this.stageColumns.map((stage, index) => [stage.id, index]))

      const items = []
      rows.forEach((row, rowIndex) => {
        const sketchId = (row[sketchHeader] || '').trim()
        if (!sketchId) return

        const order = { sketch: sketchOrder[sketchId.toUpperCase()], stage: -1 }
        const rowError = (message) => items.push({ key: `row-${rowIndex}`, sketchId, stage: '-', type: 'error', message, order })
        if (sketchIdCount[sketchId.toUpperCase()] > 1) {
          rowError('Sketch ID ซ้ำกันในไฟล์')
          return
        }
        const prototype = prototypeMap.get(sketchId.toUpperCase())
        if (!prototype) {
          rowError('ไม่พบ Sketch ID')
          return
        }

        const stageValues = {}
        Object.entries(columnMap).forEach(([header, { stageId, label, field }]) => {
          if (!stageValues[stageId]) stageValues[stageId] = { label }
          stageValues[stageId][field] = row[header] || ''
        })

        Object.entries(stageValues).forEach(([stageId, values]) => {
          const item = this.buildStageItem(prototype, stageId, values)
          if (!item) return
          if (item.type === 'unchanged') {
            this.unchangedCount += 1
            return
          }
          items.push({ key: `row-${rowIndex}-${stageId}`, sketchId, stage: values.label, ...item, order: { ...order, stage: stageOrder[stageId] } })
        })
      })

      this.previewItems = items.sort((a, b) => a.order.sketch - b.order.sketch || a.order.stage - b.order.stage)
    },
    buildStageItem (prototype, stageId, values) {
      const rawStart = values.startDate || ''
      const rawDue = values.dueDate || ''
      if (isBlank(rawStart) && isBlank(rawDue)) return null

      const task = prototype?.tasksByStage?.[stageId]
      if (!task) {
        return { type: 'skip', message: 'ไม่มี Task ใน stage นี้' }
      }

      const oldStart = task.startDate ? this.$dayjs(task.startDate) : null
      const oldDue = task.dueDate ? this.$dayjs(task.dueDate) : null
      const base = { oldStart: this.formatDate(oldStart), oldDue: this.formatDate(oldDue) }

      const newStart = isBlank(rawStart) ? null : this.parseDate(rawStart)
      const newDue = isBlank(rawDue) ? null : this.parseDate(rawDue)
      const invalid = [
        !isBlank(rawStart) && !newStart ? `Start "${rawStart}"` : '',
        !isBlank(rawDue) && !newDue ? `Due "${rawDue}"` : ''
      ].filter(Boolean)
      if (invalid.length) {
        return { ...base, type: 'error', message: `วันที่ไม่ถูกต้อง: ${invalid.join(', ')} (ใช้ DD/MM/YYYY หรือ DD/MM/YY)` }
      }

      const startChanged = !!newStart && !(oldStart && newStart.isSame(oldStart, 'day'))
      const dueChanged = !!newDue && !(oldDue && newDue.isSame(oldDue, 'day'))
      if (!startChanged && !dueChanged) return { type: 'unchanged' }

      const changed = {
        ...base,
        newStart: startChanged ? this.formatDate(newStart) : '',
        newDue: dueChanged ? this.formatDate(newDue) : ''
      }

      if (task.taskStatus !== 'pending') {
        return { ...changed, type: 'skip', message: 'Task completed แล้ว แก้วันที่ไม่ได้' }
      }

      const finalStart = startChanged ? newStart : oldStart
      const finalDue = dueChanged ? newDue : oldDue
      if (!finalStart || !finalDue) {
        return { ...changed, type: 'error', message: 'กรุณาระบุทั้ง Start และ Due' }
      }
      if (finalStart.isAfter(finalDue, 'day')) {
        return { ...changed, type: 'error', message: 'Start ต้องไม่เกิน Due' }
      }

      const payload = { id: task.id }
      if (startChanged) payload.startDate = newStart.startOf('day').toISOString()
      if (dueChanged) payload.dueDate = newDue.startOf('day').toISOString()

      return { ...changed, type: 'change', payload }
    },
    handleImport () {
      if (!this.canImport) return

      this.$emit('confirm', {
        tasks: this.previewItems.filter((item) => item.type === 'change').map((item) => item.payload),
        note: this.note.trim()
      })
    },
    closeModal () {
      if (this.loading) return

      this.isOpen = false
      this.$emit('close')
    }
  }
}
</script>
